import { AsyncPipe, CurrencyPipe, DOCUMENT, JsonPipe, NgClass } from '@angular/common';
import {
  afterNextRender,
  Component,
  DestroyRef,
  inject,
  Inject,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import {
  LegacyConfig,
  LegacyConfigService,
  LocalizationConfigService,
  StoreConfig,
  StoreConfigService,
  StoreSpecificConfigService,
} from '@scalefast/config-angular';
import { createCartProductFromProduct, Metaproduct, Product, UpdateWishlistResponse, Wishlist } from '@scalefast/core';
import { AddToCartService, ProductLogger, ProductService, WishlistService } from '@scalefast/ecommerce-core';
import { EMPTY, Observable } from 'rxjs';
import { catchError, map, shareReplay, take } from 'rxjs/operators';
import { ProductSpecificCampaign } from 'src/app/core/interfaces/page-creator';
import { AnalyticsStoreService, PohService } from 'src/app/shared/services';
import { ProductSpecificCampaignsService } from 'src/app/shared/services/product-specific-campaigns.service';
import { WindowRef } from 'src/app/shared/services/window-ref.service';
import { MISSING_PRODUCT_IMAGE_URL } from '../../app.tokens';
import { AppCrossSellProductsComponent } from '../../components/product/cross-sell-products/cross-sell-products.component';
import { AppProductCarouselComponent } from '../../components/product/product-carousel/product-carousel.component';
import { AppProductGalleryComponent } from '../../components/product/product-gallery/product-gallery.component';
import { AppProductPriceComponent } from '../../components/product/product-price/product-price.component';
import { AppAddToCartComponent } from '../../shared/components/add-to-cart/add-to-cart.component';
import { AppBazaarvoiceComponent } from '../../shared/components/bazaarvoice/bazaarvoice.component';
import { AppModalComponent } from '../../shared/components/modal/modal.component';
import { AppSchemaStructuredDataComponent } from '../../shared/components/schema-structured-data/schema-structured-data.component';
import { CleanTextHelper } from '../../shared/helpers/clean-text.helper';
import { ExtractBazaarvoiceID } from '../../shared/pipes/extract-bazaarvoice-id.pipe';
import { TranslateProductFieldPipe } from '../../shared/pipes/translate-product-field.pipe';
import { MetaTagsService, SyndigoService } from '../../shared/services/';
import { AppAddToWishlistComponent } from './add-to-wishlist/add-to-wishlist.component';
import { AppColorMetaproductComponent } from './color-metaproduct/color-metaproduct.component';
import { AppOptionsMetaproductComponent } from './options-metaproduct/options-metaproduct.component';
import { AppProductAddedToCartComponent } from './product-added-to-cart/product-added-to-cart.component';
import { AppProductBenefitsComponent } from './product-benefits/product-benefits.component';
import { AppProductBundleComponent } from './product-bundle/product-bundle.component';
import { AppProductDescriptionAccordionComponent } from './product-description-accordion/product-description-accordion.component';
import { AppProductPolicyAccordionComponent } from './product-policy-accordion/product-policy-accordion.component';
import { AppProductStockMessageComponent } from './product-stock-message/product-stock-message.component';
import { AppSpecificCampaignHighlightComponent } from './specific-campaign-highlight/specific-campaign-highlight.component';
import { AppSyndigoComponent } from './syndigo/syndigo.component';

@Component({
  selector: 'app-product',
  styleUrls: ['./product.component.scss'],
  templateUrl: './product.component.html',
  standalone: true,
  imports: [
    AppProductGalleryComponent,
    AppProductDescriptionAccordionComponent,
    AppProductPolicyAccordionComponent,
    AppBazaarvoiceComponent,
    AppProductPriceComponent,
    AppSpecificCampaignHighlightComponent,
    AppColorMetaproductComponent,
    AppOptionsMetaproductComponent,
    AppProductBundleComponent,
    AppAddToCartComponent,
    AppAddToWishlistComponent,
    RouterLink,
    AppProductStockMessageComponent,
    AppProductBenefitsComponent,
    AppSyndigoComponent,
    AppProductCarouselComponent,
    AppModalComponent,
    AppProductAddedToCartComponent,
    AppCrossSellProductsComponent,
    AppSchemaStructuredDataComponent,
    AsyncPipe,
    CurrencyPipe,
    TranslateProductFieldPipe,
    ExtractBazaarvoiceID,
    NgClass,
    JsonPipe,
  ],
})
export class AppProductComponent implements OnInit, OnDestroy {
  product$: Observable<Product> = EMPTY;
  relatedProducts$: Observable<Array<Product>> = EMPTY;
  youMayAlsoLikeProducts$: Observable<Array<Product>> = EMPTY;
  colorReferences: Record<string, string> | undefined = {};
  productTitle: string = '';
  productShortDescription: string = '';
  @ViewChild('ModalComponent') modalComponent: AppModalComponent = new AppModalComponent();
  @ViewChild('ModalErrorComponent') modalErrorComponent: AppModalComponent = new AppModalComponent();
  @ViewChild('ModalErrorComponentCartMaxValue') modalErrorComponentCartMaxValue: AppModalComponent = new AppModalComponent();
  @ViewChild('ModalEcotax') modalEcotax: AppModalComponent = new AppModalComponent();
  quantity = 1;
  product: Product | undefined;
  wishlist: Wishlist | undefined;
  isInWishlist = false;
  uniqueModalId: string = this.generateUniqueId();
  uniqueErrorModalId: string | undefined;
  addedToCartProduct: Product | undefined;
  private storeConfig: StoreConfig;
  ecoTax: number = 0;
  readonly currency;
  isLowestPrice: boolean = false;
  lowestPrice: number = 0;
  price: number = 0;
  isPohProduct: boolean = false;
  pohUrl: string = '';
  productSpecificCampaign: ProductSpecificCampaign | undefined;

  showOptions: boolean = false;
  metaproducts: Metaproduct[] | undefined;
  colorMetaproducts: Metaproduct[] = [];
  optionsMetaproducts: Metaproduct[] = [];

  MAX_ITEM_QUANTITY: number;

  modalTitle = $localize`:@@product.addedToCart:Successfully added to your cart!`;
  dynamicTitles = {
    'PRODUCTS.DESCRIPTION': $localize`:@@product.description:Description`,
    'PRODUCTS.FEATURES': $localize`:@@product.features:Features`,
  };
  public currentTemplate: string = 'product';
  private legacyConfig: LegacyConfig;

  #logger = inject(ProductLogger);

  constructor(
    @Inject(LOCALE_ID) protected locale: string,
    @Inject(DOCUMENT) private _doc: Document,
    @Inject(MISSING_PRODUCT_IMAGE_URL) public missingProductImageUrl: string,
    @Inject('WindowRef') private windowRef: WindowRef,
    @Inject(PLATFORM_ID) private platformId: Object,
    private productService: ProductService,
    private route: ActivatedRoute,
    private legacyConfigService: LegacyConfigService,
    private syndigoService: SyndigoService,
    private wishlistService: WishlistService,
    private destroyRef: DestroyRef,
    private storeConfigService: StoreConfigService,
    private addToCartService: AddToCartService,
    private analyticsStoreService: AnalyticsStoreService,
    private localizationConfigService: LocalizationConfigService,
    private metaTags: MetaTagsService,
    private pohService: PohService,
    private storeSpecificConfigService: StoreSpecificConfigService,
    private productSpecificCampaignsService: ProductSpecificCampaignsService,
  ) {
    this.legacyConfig = this.legacyConfigService.get();
    this.storeConfig = this.storeConfigService.get();
    this.MAX_ITEM_QUANTITY = this.storeConfig.maxItemQuantity;
    this.currency = this.localizationConfigService.get().currency;
    this.pohUrl = this.pohService.getUrl();
    this.product$ = this.route.data.pipe(
      map((data) => {
        this.#logger.trace('Product component data', data.product);
        return data.product;
      }),
      shareReplay(),
    );
    this.product$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((product) => {
      this.product = product;
      this.pohService.setCurrentProduct(product);
      this.isPohProduct = this.pohService.checkIfProductIsPoh(product);
      this.metaproducts = product.metaproductsV4;
      if (this.metaproducts) {
        this.colorMetaproducts = this.metaproducts.filter((metaproduct) => metaproduct.type === 'color');
        this.optionsMetaproducts = this.metaproducts.filter((metaproduct) => metaproduct.type === 'options');
      }
      this.relatedProducts$ = this.productService.getRelatedProducts$(this.product);
      this.youMayAlsoLikeProducts$ = this.productService.getYouMayAlsoLikeProducts$(this.product);
      this.productTitle = this.translateProductField(product.descriptions, 'title');
      this.productShortDescription = this.translateProductField(product.descriptions, 'short');
      this.setEcotax();
      this.setIsLowestPrice();
      this.setLowestPrice();
      this.setPrice();
      this.setMetaTags();
      this.productSpecificCampaignsService
        .getProductSpecificCampaigns(this.product.id, this.product.refID ?? '')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((productSpecificCampaigns) => {
          this.productSpecificCampaign = productSpecificCampaigns[0];
        });
    });
    this.analyticsStoreService.pg_content(this.currentTemplate);
    this.syndigoService.injectScript();

    this.colorReferences = this.legacyConfig.metacolorReferences;

    this.getWishlist();
    afterNextRender(() => {
      this.windowRef.nativeWindow.scrollTo({
        top: 0,
        left: 0,
        behavior: 'instant',
      });
    });
  }

  ngOnInit(): void {
    this._doc.body.classList.add('product-page');
  }

  ngOnDestroy(): void {
    this._doc.body.classList.remove('product-page');
    this.pohService.setCurrentProduct(undefined);
  }

  get productFeeds() {
    return this.product!.v4ApiResponse?.feeds?.map((feed: any) => feed.feedID);
  }

  generateUniqueId(): string {
    return Math.random().toString();
  }

  translateProductField(prodDescription: any[], field: string): string {
    const description = prodDescription.find((item) => item.lang.toLowerCase() === this.locale);
    return description ? description[field] : prodDescription[0][field];
  }

  setShowOptions(value: boolean) {
    this.showOptions = value;
  }

  openModalEcotax() {
    this.modalEcotax.show();
  }

  openAddedToCartModal(quantity: number): void {
    // total quantity of the product on the cart
    if (!this.product) {
      return;
    }
    // If quantity is -2 it means the max order value was reached and no product of that type was added to the cart
    if (quantity === -2) {
      this.uniqueErrorModalId = this.generateUniqueId();
      this.modalErrorComponentCartMaxValue.show();
      return;
    }

    // If quiantity is -1 it means that max product value was reached and no product was added to the cart
    if (quantity === -1) {
      this.uniqueErrorModalId = this.generateUniqueId();
      this.modalErrorComponent.show();
      return;
    }

    this.quantity = quantity;

    this.addedToCartProduct = this.product;
    this.uniqueModalId = this.generateUniqueId();
    this.modalComponent.show();
  }

  closeAddedToCartModal(): void {
    this.modalComponent.close();
  }

  closeQuantityErrorModal(): void {
    this.modalErrorComponent.close();
  }

  closeQuantityErrorCartMaxValueModal(): void {
    this.modalErrorComponentCartMaxValue.close();
  }

  getWishlist(): void {
    const wishlist$ = this.wishlistService.getWishlist$();
    wishlist$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((wishlist) => {
      this.wishlist = wishlist;
      if (!this.wishlist) {
        this.isInWishlist = false;
        return;
      }

      this.isInWishlist = this.wishlist.products.some((product) => this.product?.id == product.productID);
    });
  }

  addToWishlistEventHandler(): void {
    if (this.wishlist && this.product) {
      this.wishlistService
        .addIntoWishlist$(this.wishlist, this.product)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response: UpdateWishlistResponse) => {
          if (response.response_data) {
            this.isInWishlist = true;
            this.getWishlist();
          }
        });
    }
  }

  removeFromWishlistEventHandler(): void {
    if (this.wishlist && this.product) {
      this.wishlistService
        .removeFromWishlist$(this.wishlist, this.product)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response: UpdateWishlistResponse) => {
          if (response.response_data) {
            this.isInWishlist = false;
            this.getWishlist();
          }
        });
    }
  }

  checkLoggedUserEventHandler(): void {
    this.wishlistService.checkLoggedUser();
  }

  addCrossSellProductToCart(crossSellProduct: Product): void {
    const cartCrossSellProduct = createCartProductFromProduct(crossSellProduct);
    this.addToCartService
      .onAddToCart(cartCrossSellProduct)
      .pipe(
        catchError((err: unknown) => {
          console.log(err);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.addedToCartProduct = crossSellProduct;
  }

  setEcotax(): void {
    if (this.product?.v4ApiResponse?.price_info.ecotax != 0) {
      this.ecoTax = this.product?.v4ApiResponse.price_info.ecotax;
    }
  }
  setIsLowestPrice(): void {
    if (this.product?.v4ApiResponse?.has_lowest_price === '1') {
      this.isLowestPrice = true;
    }
  }

  setLowestPrice(): void {
    if (this.product?.v4ApiResponse?.price_info.prices[0].min_30_days_price) {
      this.lowestPrice = this.product?.v4ApiResponse?.price_info.prices[0].min_30_days_price;
    }
  }
  setPrice(): void {
    if (this.product?.v4ApiResponse?.price_info.prices) {
      this.price = this.product?.v4ApiResponse?.price_info.prices[0].price;
    }
  }
  private setMetaTags(): void {
    let description = '';
    description = this.productShortDescription + '. ' + this.translateProductField(this.product?.descriptions ?? [], 'long');
    this.metaTags.setMetaTags({
      title: this.productTitle,
      imageSrc: this.product?.imageUrl ?? '',
      description: CleanTextHelper.removeHtml(description, this._doc).substring(0, 317) + '...',
    });
  }
}
